import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import loadImage from '../assets/imageLoader';
import apiClient from '../api/api';
import { useAuth } from '../context/AuthContext';
import { FaEdit, FaSave, FaTimes, FaCamera, FaCertificate } from 'react-icons/fa';

function BrokerProfilePage() {
    const { id } = useParams();
    const { user } = useAuth();
    const [broker, setBroker] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isEditing, setIsEditing] = useState(false);

    // Form Data State
    const [formData, setFormData] = useState({});
    const [uploadedImage, setUploadedImage] = useState(null);
    const [previewImage, setPreviewImage] = useState(null);

    // Dropdown Data States
    const [divisions, setDivisions] = useState([]);
    const [districts, setDistricts] = useState([]);
    const [upazilas, setUpazilas] = useState([]);

    // Determine if we are viewing our own profile
    const isOwnProfile = !id || (user?.userId && user.userId === id);
    const userIdToFetch = id || user?.userId;

    // Fetch Initial Data (Divisions)
    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                const divResponse = await apiClient.get('/location-data/divisions');
                setDivisions(divResponse.data || []);
            } catch (err) {
                console.error("Failed to load dropdown data", err);
            }
        };
        fetchInitialData();
    }, []);

    // Fetch Districts when Division changes
    useEffect(() => {
        if (!formData.division) {
            setDistricts([]);
            return;
        }
        apiClient.get(`/location-data/districts/${formData.division}`)
            .then(res => setDistricts(res.data))
            .catch(err => console.error(err));
    }, [formData.division]);

    // Fetch Upazilas when District changes
    useEffect(() => {
        if (!formData.district) {
            setUpazilas([]);
            return;
        }
        apiClient.get(`/location-data/thanas/${formData.district}`)
            .then(res => setUpazilas(res.data))
            .catch(err => console.error(err));
    }, [formData.district]);

    // Fetch Broker Profile
    useEffect(() => {
        const fetchBroker = async () => {
            if (!userIdToFetch) return;
            try {
                setLoading(true);
                const response = await apiClient.get(`/profiles/${userIdToFetch}`);
                const brokerData = response.data;

                setBroker(brokerData);

                // Initialize Form Data
                setFormData({
                    name: brokerData.name,
                    email: brokerData.User?.email || '',
                    date_of_birth: brokerData.date_of_birth ? new Date(brokerData.date_of_birth).toISOString().split('T')[0] : '',
                    gender: brokerData.gender,
                    nid_or_passport: brokerData.nid_or_passport,
                    division: brokerData.location?.division || '',
                    district: brokerData.location?.district || '',
                    upazila: brokerData.location?.upazila || '',
                    granular_location: brokerData.location?.granular_location || '',
                });
                setPreviewImage(loadImage(brokerData.picture));
            } catch (err) {
                setError("Failed to load broker profile.");
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        fetchBroker();
    }, [userIdToFetch]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleImageChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            if (file.size > 1024 * 1024) {
                alert("Image size should not exceed 1MB.");
                return;
            }
            setUploadedImage(file);
            setPreviewImage(URL.createObjectURL(file));
        }
    };

    const handleSave = async () => {
        try {
            setLoading(true);
            let imageUrl = broker.picture;

            // 1. Upload Image if changed
            if (uploadedImage) {
                const imageFormData = new FormData();
                imageFormData.append('profilePicture', uploadedImage);
                const uploadResponse = await apiClient.post('/upload/image', imageFormData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                });
                imageUrl = uploadResponse.data.filePath;
            }

            // 2. Construct Payload
            const payload = {
                profile: {
                    name: formData.name,
                    date_of_birth: formData.date_of_birth,
                    gender: formData.gender,
                    nid_or_passport: formData.nid_or_passport,
                    picture: imageUrl,
                    location: {
                        division: formData.division,
                        district: formData.district,
                        upazila: formData.upazila,
                        granular_location: formData.granular_location
                    }
                },
                // Only include email if it was originally missing and user provided one
                ...(broker.User?.email ? {} : { email: formData.email })
            };

            await apiClient.put('/profiles/complete', payload);

            // Refresh data
            const response = await apiClient.get(`/profiles/${userIdToFetch}`);
            const brokerData = response.data;

            setBroker(brokerData);

            // Update local state
            setFormData(prev => ({
                ...prev,
                email: brokerData.User?.email || prev.email,
            }));

            setIsEditing(false);
            setUploadedImage(null);
            alert('Profile updated successfully!');

        } catch (err) {
            console.error("Failed to update profile:", err);
            alert(err.response?.data?.message || 'Failed to update profile. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Helper to format date for display
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString();
    };

    if (loading) return <div className="text-center py-10">Loading...</div>;
    if (error) return <div className="text-center py-10 text-red-500">{error}</div>;
    if (!broker) return <div className="text-center py-10">Broker not found</div>;

    return (
        <div className="bg-gray-100 min-h-screen p-4 sm:p-6 lg:p-8">
            <div className="container mx-auto">
                <div className="flex justify-between items-center mb-6">
                    <h1 className="text-3xl font-bold text-gray-800">
                        {isOwnProfile ? 'My Profile' : broker.name}
                    </h1>
                    {isOwnProfile && !isEditing && (
                        <button
                            onClick={() => setIsEditing(true)}
                            className="flex items-center bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                        >
                            <FaEdit className="mr-2" /> Edit Profile
                        </button>
                    )}
                    {isEditing && (
                        <div className="space-x-2">
                            <button
                                onClick={handleSave}
                                className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
                            >
                                <FaSave className="mr-2 inline" /> Save
                            </button>
                            <button
                                onClick={() => {
                                    setIsEditing(false);
                                    setPreviewImage(loadImage(broker.picture)); // Reset image preview
                                    setUploadedImage(null);
                                }}
                                className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600"
                            >
                                <FaTimes className="mr-2 inline" /> Cancel
                            </button>
                        </div>
                    )}
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Left Column */}
                    <div className="lg:col-span-1">
                        <div className="bg-white rounded-lg shadow-lg p-6">
                            <div className="flex flex-col items-center relative">
                                <div className="relative">
                                    <img
                                        src={previewImage || loadImage(broker.picture)}
                                        alt={broker.name}
                                        className="w-32 h-32 rounded-full object-cover border-4 border-blue-500"
                                    />
                                    {isEditing && (
                                        <label htmlFor="profile-upload" className="absolute bottom-0 right-0 bg-blue-600 text-white p-2 rounded-full cursor-pointer hover:bg-blue-700">
                                            <FaCamera size={16} />
                                            <input
                                                type="file"
                                                id="profile-upload"
                                                accept="image/*"
                                                onChange={handleImageChange}
                                                className="hidden"
                                            />
                                        </label>
                                    )}
                                </div>

                                {isEditing ? (
                                    <input
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        className="mt-4 text-center border rounded p-1 w-full font-bold text-lg"
                                        placeholder="Full Name"
                                    />
                                ) : (
                                    <h2 className="mt-4 text-2xl font-bold text-gray-800">{broker.name}</h2>
                                )}

                                <p className="text-gray-600 text-center mt-2">Broker</p>
                            </div>

                            <div className="mt-6 border-t pt-4">
                                <h3 className="font-semibold text-gray-700 mb-2">Contact Info</h3>
                                <div className="mb-2">
                                    <p className="text-sm text-gray-600"><strong>Phone:</strong> {broker.User?.phone_number || 'N/A'} <span className="text-xs text-gray-400">(Read-Only)</span></p>
                                </div>
                                <div>
                                    <strong className="text-sm text-gray-600">Email:</strong>
                                    {isEditing && !broker.User?.email ? (
                                        <input
                                            type="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleInputChange}
                                            className="border rounded p-1 w-full mt-1 text-sm"
                                            placeholder="Enter your email"
                                        />
                                    ) : (
                                        <span className="text-sm text-gray-600 ml-1">{broker.User?.email || 'N/A'} {broker.User?.email && <span className="text-xs text-gray-400">(Read-Only)</span>}</span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right Column */}
                    <div className="lg:col-span-2">
                        <div className="bg-white rounded-lg shadow-lg p-6 space-y-6">

                            {/* Personal Details */}
                            <div>
                                <h3 className="text-lg font-semibold text-gray-800 border-b pb-2 mb-4">Personal Details</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Date of Birth</label>
                                        {isEditing ? (
                                            <input type="date" name="date_of_birth" value={formData.date_of_birth || ''} onChange={handleInputChange} className="border rounded p-1 w-full" />
                                        ) : <p>{formatDate(broker.date_of_birth)}</p>}
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Gender</label>
                                        {isEditing ? (
                                            <select name="gender" value={formData.gender || ''} onChange={handleInputChange} className="border rounded p-1 w-full">
                                                <option value="Male">Male</option>
                                                <option value="Female">Female</option>
                                                <option value="Other">Other</option>
                                            </select>
                                        ) : <p>{broker.gender}</p>}
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">NID / Passport</label>
                                        {isEditing ? (
                                            <input type="text" name="nid_or_passport" value={formData.nid_or_passport || ''} onChange={handleInputChange} className="border rounded p-1 w-full" />
                                        ) : <p>{broker.nid_or_passport}</p>}
                                    </div>
                                </div>
                            </div>

                            {/* Address */}
                            <div>
                                <h3 className="text-lg font-semibold text-gray-800 border-b pb-2 mb-4">Address</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Division</label>
                                        {isEditing ? (
                                            <select name="division" value={formData.division} onChange={handleInputChange} className="border rounded p-1 w-full">
                                                <option value="">Select Division</option>
                                                {divisions.map(d => <option key={d} value={d}>{d}</option>)}
                                            </select>
                                        ) : <p>{broker.location?.division}</p>}
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">District</label>
                                        {isEditing ? (
                                            <select name="district" value={formData.district} onChange={handleInputChange} className="border rounded p-1 w-full" disabled={!formData.division}>
                                                <option value="">Select District</option>
                                                {districts.map(d => <option key={d} value={d}>{d}</option>)}
                                            </select>
                                        ) : <p>{broker.location?.district}</p>}
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Upazila</label>
                                        {isEditing ? (
                                            <select name="upazila" value={formData.upazila} onChange={handleInputChange} className="border rounded p-1 w-full" disabled={!formData.district}>
                                                <option value="">Select Upazila</option>
                                                {upazilas.map(u => <option key={u} value={u}>{u}</option>)}
                                            </select>
                                        ) : <p>{broker.location?.upazila}</p>}
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Area / Village</label>
                                        {isEditing ? (
                                            <input type="text" name="granular_location" value={formData.granular_location} onChange={handleInputChange} className="border rounded p-1 w-full" />
                                        ) : <p>{broker.location?.granular_location}</p>}
                                    </div>
                                </div>
                            </div>

                            {/* Broker Certificate */}
                            <div>
                                <h3 className="text-lg font-semibold text-gray-800 border-b pb-2 mb-4 flex items-center">
                                    <FaCertificate className="mr-2 text-blue-500" /> Broker Certificate
                                </h3>
                                {broker.broker_details?.broker_license ? (
                                    <div className="border rounded-lg p-4 bg-gray-50">
                                        <img
                                            src={loadImage(broker.broker_details.broker_license)}
                                            alt="Broker Certificate"
                                            className="max-w-full h-auto rounded shadow-sm"
                                        />
                                    </div>
                                ) : (
                                    <p className="text-gray-500 italic">No certificate uploaded.</p>
                                )}
                            </div>

                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default BrokerProfilePage;
